import { Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import {
  SALES_INSIGHTS_OTHERS_KEY,
  SalesInsightsBreakdownResponse,
  SalesInsightsBreakdownRow,
  SalesInsightsFilterValues
} from '../../models/sales-insights.model';
import { SalesInsightsDelta, formatSalesInsightsAmount, salesInsightsDelta } from '../../utils/sales-insights-format';
import { salesInsightsFilterLabel, salesInsightsNoValueLabel } from '../../utils/sales-insights-labels';
import { SALES_INSIGHTS_SEARCH_MIN_ROWS, salesInsightsMatches } from '../../utils/sales-insights-search';
import { SalesInsightsBaseComponent } from '../sales-insights-base/sales-insights-base.component';

type MeasureColumn = 'valueBase' | 'valueLocal' | 'quantity' | 'rowCount';
type SalesInsightsTableColumn = 'name' | MeasureColumn;
type SortDirection = 'asc' | 'desc';

const MEASURE_COLUMNS: MeasureColumn[] = ['valueBase', 'valueLocal', 'quantity', 'rowCount'];
const DEFAULT_MEASURE_COLUMNS: MeasureColumn[] = ['valueBase', 'quantity', 'rowCount'];

@Component({
  selector: 'lib-sales-insights-table',
  standalone: false,
  templateUrl: './sales-insights-table.component.html',
  styleUrl: './sales-insights-table.component.scss'
})
export class SalesInsightsTableComponent extends SalesInsightsBaseComponent<SalesInsightsBreakdownResponse> {
  protected translate = inject(TranslateService);

  readonly othersKey = SALES_INSIGHTS_OTHERS_KEY;
  readonly formatAmount = formatSalesInsightsAmount;

  rows: SalesInsightsBreakdownRow[] = [];
  query = '';
  private allRows: SalesInsightsBreakdownRow[] = [];
  sortColumn: SalesInsightsTableColumn = 'valueBase';
  sortDirection: SortDirection = 'desc';
  exporting = false;

  // key, name and currency from the catalog's columns are always shown in the name cell, not as columns.
  get measureColumns(): MeasureColumn[] {
    const configured = ((this.options.columns || []) as string[]).filter((column): column is MeasureColumn =>
      MEASURE_COLUMNS.includes(column as MeasureColumn)
    );
    return configured.length ? configured : DEFAULT_MEASURE_COLUMNS;
  }

  get groupBy(): string | undefined {
    return this.config?.source?.params?.groupBy as string | undefined;
  }

  get nameHeader(): string {
    return this.groupBy
      ? salesInsightsFilterLabel(this.translate, this.groupBy)
      : this.translate.instant('SALES_INSIGHTS_COL_NAME');
  }

  get compares(): boolean {
    return this.options.compare === 'prev-year';
  }

  get compareColumn(): MeasureColumn {
    return this.measureColumns[0];
  }

  previousValue(row: SalesInsightsBreakdownRow): number | null {
    return row.previous?.[this.compareColumn] ?? null;
  }

  change(row: SalesInsightsBreakdownRow): SalesInsightsDelta | null {
    return salesInsightsDelta(row[this.compareColumn], this.previousValue(row));
  }

  get canExport(): boolean {
    return !!this.options.export && !this.loading && !this.error && this.rows.length > 0;
  }

  get columnCount(): number {
    return 1 + this.measureColumns.length + (this.compares ? 2 : 0);
  }

  get searchable(): boolean {
    return this.options.search !== false && !this.loading && this.allRows.length >= SALES_INSIGHTS_SEARCH_MIN_ROWS;
  }

  search(query: string): void {
    this.query = query;
    this.rows = this.sortRows(this.matching());
  }

  protected defaultSubtitle(): string {
    return this.periodLabelKey();
  }

  protected fetch(filters: SalesInsightsFilterValues): Observable<SalesInsightsBreakdownResponse> {
    if (!this.compares || !this.config.source) {
      return super.fetch(filters);
    }
    return this.salesInsightsService.getData<SalesInsightsBreakdownResponse>(
      { ...this.config.source, params: { ...(this.config.source.params || {}), compare: 'prev-year' } },
      filters,
      this.config.accepts
    );
  }

  protected isEmpty(data: SalesInsightsBreakdownResponse | null): boolean {
    return !data?.rows?.length;
  }

  protected onData(data: SalesInsightsBreakdownResponse | null): void {
    this.allRows = data?.rows || [];
    this.rows = this.sortRows(this.matching());
  }

  private matching(): SalesInsightsBreakdownRow[] {
    return this.allRows.filter(row => salesInsightsMatches(this.query, [this.displayName(row), row.key]));
  }

  sortBy(column: SalesInsightsTableColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = column === 'name' ? 'asc' : 'desc';
    }
    this.rows = this.sortRows(this.rows);
  }

  displayName(row: SalesInsightsBreakdownRow): string {
    if (row.key === SALES_INSIGHTS_OTHERS_KEY) {
      return this.translate.instant('SALES_INSIGHTS_OTHERS');
    }
    if (!row.key) {
      return salesInsightsNoValueLabel(this.translate, this.groupBy);
    }
    return row.name || row.key;
  }

  showsCode(row: SalesInsightsBreakdownRow): boolean {
    return !!row.key && !!row.name && row.name !== row.key && row.key !== SALES_INSIGHTS_OTHERS_KEY;
  }

  columnLabelKey(column: SalesInsightsTableColumn): string {
    return `SALES_INSIGHTS_COL_${column.replace(/([A-Z])/g, '_$1').toUpperCase()}`;
  }

  async exportExcel(): Promise<void> {
    this.exporting = true;
    try {
      const XLSX = await import('xlsx');
      const compareHeader = this.compares
        ? [this.translate.instant('SALES_INSIGHTS_COL_PREVIOUS'), this.translate.instant('SALES_INSIGHTS_COL_CHANGE')]
        : [];
      const header = [
        this.nameHeader,
        ...this.measureColumns.map(c => this.translate.instant(this.columnLabelKey(c))),
        ...compareHeader
      ];
      const body = this.rows.map(row => [
        this.displayName(row),
        ...this.measureColumns.map(c => row[c] ?? null),
        ...(this.compares ? [this.previousValue(row), this.change(row)?.percent ?? null] : [])
      ]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([header, ...body]), 'Data');
      XLSX.writeFile(workbook, `${this.fileName()}.xlsx`);
    } finally {
      this.exporting = false;
    }
  }

  private fileName(): string {
    const title = this.translate.instant(this.config?.title || 'SALES_INSIGHTS');
    return title.replace(/[\\/:*?"<>|]+/g, '').trim() || 'sales-insights';
  }

  private sortRows(rows: SalesInsightsBreakdownRow[]): SalesInsightsBreakdownRow[] {
    const factor = this.sortDirection === 'asc' ? 1 : -1;
    const others = rows.filter(row => row.key === SALES_INSIGHTS_OTHERS_KEY);
    const regular = rows
      .filter(row => row.key !== SALES_INSIGHTS_OTHERS_KEY)
      .sort((a, b) => {
        if (this.sortColumn === 'name') {
          return this.displayName(a).localeCompare(this.displayName(b), 'sv') * factor;
        }
        const column = this.sortColumn as MeasureColumn;
        return ((a[column] ?? 0) - (b[column] ?? 0)) * factor;
      });
    return [...regular, ...others];
  }
}
